/**
 * ScriptPlayHeader Component - MA-ARCH-162
 * 
 * Extracted from ScriptPlay.jsx - Top header with script title and action controls.
 * Handles sandbox toggle, restart, edit, and end run actions.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  RefreshCw,
  Pencil,
  Flag,
  FlaskConical,
} from 'lucide-react';

/**
 * @param {Object} props
 * @param {Object} props.script - Script object with name, product_type_id
 * @param {boolean} props.sandboxMode - Whether sandbox mode is active
 * @param {Function} props.onSandboxToggle - Callback when sandbox is toggled
 * @param {boolean} props.canUseSandbox - Whether user can use sandbox mode
 * @param {boolean} props.hasStartedRun - Whether a run is in progress
 * @param {boolean} props.isAgentRole - Whether user is agent role (limits edit)
 * @param {Function} props.onRestart - Callback to restart run
 * @param {Function} props.onEdit - Callback to navigate to edit
 * @param {Function} props.onEndRun - Callback to end the run
 */
export function ScriptPlayHeader({
  script,
  sandboxMode,
  onSandboxToggle,
  canUseSandbox,
  hasStartedRun,
  isAgentRole,
  onRestart,
  onEdit,
  onEndRun,
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1
              className="text-2xl font-semibold text-slate-100"
              data-testid="script-play-title"
            >
              {script?.name || "Script"}
            </h1>
          </div>
        </div>
      </div>

      <div
        className="ml-auto flex items-center gap-2"
        data-testid="script-play-actions"
      >
        {/* Sandbox Mode Toggle - Only for Admins/Super Admins */}
        {canUseSandbox && (
          <div
            className={
              "flex items-center gap-2 rounded-full px-3 py-2 ring-1 " +
              (sandboxMode
                ? "bg-amber-500/20 ring-amber-400/30"
                : "bg-white/5 ring-white/10")
            }
            data-testid="sandbox-toggle-container"
          >
            <FlaskConical
              className={`h-4 w-4 ${sandboxMode ? "text-amber-400" : "text-slate-400"}`}
            />
            <span
              className={`text-sm ${sandboxMode ? "text-amber-300" : "text-slate-300"}`}
            >
              Sandbox
            </span>
            <Switch
              checked={sandboxMode}
              variant="sandbox"
              onCheckedChange={onSandboxToggle}
              data-testid="sandbox-toggle"
            />
          </div>
        )}

        <Button
          variant="secondary"
          size="icon"
          className="h-10 w-10 rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
          onClick={onRestart}
          data-testid="restart-script-button"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>

        <Button
          className={
            "h-10 w-10 rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500 " +
            (isAgentRole ? "opacity-60" : "")
          }
          onClick={onEdit}
          data-testid="go-to-builder-button"
          disabled={isAgentRole}
        >
          <Pencil className="h-4 w-4" />
        </Button>

        <Button
          variant="secondary"
          size="icon"
          className="h-10 w-10 rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
          onClick={onEndRun}
          data-testid="end-run-button"
        >
          <Flag className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default ScriptPlayHeader;
